import React, { useState, useCallback, useEffect } from 'react'
import { connect } from 'react-redux'
import axios from 'axios'
import TopRail from './TopRail'
import PageHeader from './PageHeader'
import AlertBanner from './AlertBanner'
import StatCards from './StatCards'
import ModuleCards from './ModuleCards'
import UnclaimedWorkPanel from './UnclaimedWorkPanel'
import QuickLinks from './QuickLinks'
import ConnectedSystems from './ConnectedSystems'
import SidebarPanels from './SidebarPanels'
import CommDrawer from './CommDrawer'
import { IconCheck } from './icons'
import { socket } from '../../SocketIo/Socket'
import '../../../styles/hmiReskin.css'
import './dashboardV2.css'

function HMIDashboardV2(props) {
  const { url } = props
  const [alertDismissed, setAlertDismissed] = useState(false)
  const [unclaimedExpanded, setUnclaimedExpanded] = useState(true)
  const [toastVisible, setToastVisible] = useState(false)
  const [toastMessage, setToastMessage] = useState('')
  const [unclaimedItems, setUnclaimedItems] = useState([])
  const [unclaimedStats, setUnclaimedStats] = useState({ total: 0, urgent: 0 })
  const [bidDeadlines, setBidDeadlines] = useState([])
  const [activityItems, setActivityItems] = useState([])
  const [attentionItems, setAttentionItems] = useState([])
  const [dashStats, setDashStats] = useState(null)
  const [commData, setCommData] = useState(null)
  const [rcConfig, setRcConfig] = useState(null)
  const [commDrawerOpen, setCommDrawerOpen] = useState(false)

  const openCommDrawer = useCallback(() => setCommDrawerOpen(true), [])
  const closeCommDrawer = useCallback(() => setCommDrawerOpen(false), [])

  const refreshRcConfig = useCallback(() => {
    if (!url) return
    axios.get(`${url}/api/hmi/dashboard/rc-config`).then(res => {
      if (res.data?.success) setRcConfig(res.data.data)
    }).catch(() => {})
  }, [url])

  const showToast = useCallback((msg) => {
    setToastMessage(msg || 'Work item submitted to unclaimed queue')
    setToastVisible(true)
    setTimeout(() => setToastVisible(false), 3000)
  }, [])

  // Fetch unclaimed items on mount
  useEffect(() => {
    if (!url) return
    axios.get(`${url}/api/hmi/unclaimed-work`).then(res => {
      if (res.data?.success) setUnclaimedItems(res.data.data)
    }).catch(err => console.error('[DASHBOARD] Failed to load unclaimed items:', err))

    axios.get(`${url}/api/hmi/unclaimed-work/stats`).then(res => {
      if (res.data?.success) setUnclaimedStats(res.data.data)
    }).catch(err => console.error('[DASHBOARD] Failed to load unclaimed stats:', err))

    axios.get(`${url}/api/hmi/dashboard/stats`).then(res => {
      if (res.data?.success) setDashStats(res.data.data)
    }).catch(err => console.error('[DASHBOARD] Failed to load stats:', err))

    axios.get(`${url}/api/hmi/dashboard/bid-deadlines`).then(res => {
      if (res.data?.success) setBidDeadlines(res.data.data)
    }).catch(err => console.error('[DASHBOARD] Failed to load bid deadlines:', err))

    axios.get(`${url}/api/hmi/dashboard/activity`).then(res => {
      if (res.data?.success) setActivityItems(res.data.data)
    }).catch(err => console.error('[DASHBOARD] Failed to load activity:', err))

    axios.get(`${url}/api/hmi/dashboard/attention`).then(res => {
      if (res.data?.success) setAttentionItems(res.data.data)
    }).catch(err => console.error('[DASHBOARD] Failed to load attention items:', err))

    axios.get(`${url}/api/hmi/dashboard/communications`).then(res => {
      if (res.data?.success) setCommData(res.data.data)
    }).catch(err => console.error('[DASHBOARD] Failed to load communications:', err))

    axios.get(`${url}/api/hmi/dashboard/rc-config`).then(res => {
      if (res.data?.success) setRcConfig(res.data.data)
    }).catch(err => console.error('[DASHBOARD] Failed to load RC config:', err))
  }, [url])

  // Listen for real-time unclaimed work + presence updates
  useEffect(() => {
    const handleNew = (data) => {
      if (data?.type === 'unclaimed_work_new' && data.payload) {
        setUnclaimedItems(prev => [data.payload, ...prev].slice(0, 20))
        setUnclaimedStats(prev => ({ ...prev, total: prev.total + 1 }))
      }
      if (data?.type === 'unclaimed_work_assigned' && data.payload) {
        setUnclaimedItems(prev => prev.filter(i => i._id !== data.payload._id))
        setUnclaimedStats(prev => ({ ...prev, total: Math.max(0, prev.total - 1) }))
      }
      if (data?.type === 'rc_presence_update' && data.payload) {
        setCommData(prev => {
          if (!prev) return prev
          const updated = { ...prev }
          const presence = [...(updated.presence || [])]
          const idx = presence.findIndex(p => p.extensionId === data.payload.extensionId)
          if (idx >= 0) {
            presence[idx] = { ...presence[idx], ...data.payload }
          } else {
            presence.push(data.payload)
          }
          updated.presence = presence
          return updated
        })
      }
    }
    socket.on('message', handleNew)
    return () => socket.off('message', handleNew)
  }, [])

  const handleSubmit = useCallback(async (formData) => {
    try {
      const res = await axios.post(`${url}/api/hmi/unclaimed-work`, formData)
      if (res.data?.success) {
        showToast('Work item submitted to unclaimed queue')
        // Refresh the list
        const listRes = await axios.get(`${url}/api/hmi/unclaimed-work`)
        if (listRes.data?.success) setUnclaimedItems(listRes.data.data)
        const statsRes = await axios.get(`${url}/api/hmi/unclaimed-work/stats`)
        if (statsRes.data?.success) setUnclaimedStats(statsRes.data.data)
        return true
      }
    } catch (err) {
      console.error('[DASHBOARD] Submit failed:', err)
      showToast('Failed to submit — please try again')
    }
    return false
  }, [url, showToast])

  const handleSaveDraft = useCallback(async (formData) => {
    try {
      const res = await axios.post(`${url}/api/hmi/unclaimed-work`, { ...formData, state: 'draft' })
      if (res.data?.success) {
        showToast('Draft saved')
        return true
      }
    } catch (err) {
      console.error('[DASHBOARD] Save draft failed:', err)
    }
    return false
  }, [url, showToast])

  return (
    <div className="hmi-page dashboard-v2">
      {/* Legacy dashboard link banner */}
      <div className="legacy-banner">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" />
        </svg>
        <span>You&apos;re viewing the <b>new dashboard</b> (beta).</span>
        <a href="/hmi">Switch back to legacy dashboard &rarr;</a>
      </div>

      <TopRail
        onQuickCalc={() => { /* TODO: open Quick Calculator modal */ }}
        onQuickQuote={() => { /* TODO: open Quick Quote modal */ }}
      />
      <PageHeader />

      <div className="main-wrap">
        {!alertDismissed && (
          <AlertBanner
            onDismiss={() => setAlertDismissed(true)}
            commStats={commData?.summary}
            onOpenComm={openCommDrawer}
          />
        )}

        <StatCards dashStats={dashStats} />

        <div className="dash-grid">
          {/* Left column */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <ModuleCards
              commStats={commData?.summary}
              onOpenComm={openCommDrawer}
            />

            <QuickLinks />

            <UnclaimedWorkPanel
              url={url}
              expanded={unclaimedExpanded}
              onToggle={() => setUnclaimedExpanded(prev => !prev)}
              onSubmit={handleSubmit}
              onSaveDraft={handleSaveDraft}
              items={unclaimedItems}
              stats={unclaimedStats}
            />

            <ConnectedSystems />
          </div>

          {/* Right column — sidebar */}
          <SidebarPanels
            attentionItems={attentionItems}
            activityItems={activityItems}
            bidDeadlines={bidDeadlines}
            commData={commData}
            onOpenCommDrawer={openCommDrawer}
          />
        </div>
      </div>

      {/* Communications Drawer */}
      <CommDrawer
        open={commDrawerOpen}
        onClose={closeCommDrawer}
        commData={commData}
        rcConfig={rcConfig}
        url={url}
        onRefreshConfig={refreshRcConfig}
      />

      {/* Toast notification */}
      <div className={`dashboard-toast${toastVisible ? ' show' : ''}`}>
        <IconCheck size={14} />
        {toastMessage}
      </div>
    </div>
  )
}

const mapStateToProps = (state) => ({
  url: state.url,
  authenticated: state.authenticated,
})

export default connect(mapStateToProps)(HMIDashboardV2)
